import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Row, Col, Tabs, Form } from 'react-bootstrap';
import { ToastContainer, toast } from "react-toastify";
import {
    faFileZipper,
    faXmark,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import useBreakpoint from '../../../hooks/useBreakpoint';
import { Spinner } from 'react-bootstrap';

import GET_OUTGOING_TRAINEESHIP from '../../../graphql/queries/traineeships/getOutgoingTraineeship';
import ACCEPT_TRAINEESHIP from '../../../graphql/mutations/traineeships/acceptTraineeship';
import DECLINE_TRAINEESHIP from '../../../graphql/mutations/traineeships/declineTraineeship';
import UPDATE_TRAINEESHIP_TABLE_D from "../../../graphql/mutations/traineeships/updateTraineeshipTableD";

import CustomTab from '../../../components/CustomTab';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../../components/CustomModal';
import TraineeshipInfoTable from '../../../pages/Traineeships/View/components/TraineeshipInfoTable';
import StudentInfo from '../../../pages/Traineeships/View/components/StudentInfo';
import InstitutionInfoTable from '../../../pages/Traineeships/View/components/InstitutionInfoTable';
import TraineeshipProgrammeTable from '../../../pages/Traineeships/View/components/TraineeshipProgrammeTable';
import TraineeshipRecognitionTable from '../../../pages/Traineeships/View/components/TraineeshipRecognitionTable';
import TraineeshipSupportTable from '../../../pages/Traineeships/View/components/TraineeshipSupportTable';
import TraineeshipChangesTable from '../../../pages/Traineeships/View/components/TraineeshipChangesTable';
import CertificateTable from '../../../pages/Traineeships/View/components/CertificateTable';
import { StatusActionPair } from '../../../data/statusActionPairs';
import COLORS from '../../../data/colors';
import toastConfiguration from '../../../data/toastConfiguration';

import "react-toastify/dist/ReactToastify.css";
import '../../../pages/Traineeships/View/index.scss';

const GuestSingleViewTraineeship = ({ traineeshipID, guestEmail, guestName, verifiedTraineeshipID }) => {
    // const { t } = useTranslation();
    const [size] = useBreakpoint();
    const [showModalReject, setShowModalReject] = useState(false);
    const [showModalSign, setShowModalSign] = useState(false);
    const [showCreateCertificate, setShowCreateCertificate] = useState(false);
    const [rejectionMessage, setRejectionMessage] = useState("");
    const [signed, setSigned] = useState(false);
    const [validated, setValidated] = useState(false);

    const [certificateData, setCertificateData] = useState({
        field_tr_tbl_d_trainee: "",
        field_tr_tbl_d_title: "",
        field_tr_tbl_d_program: "",
        field_tr_tbl_d_rec_org: "",
        field_tr_tbl_d_rec_org_add: "",
        field_tr_tbl_d_rec_org_sec: "",
        field_tr_tbl_d_rec_org_web: "",
        field_tr_tbl_d_sta_per_physical: "",
        field_tr_tbl_d_end_per_physical: "",
        field_tr_tbl_d_sta_per_complete: "",
        field_tr_tbl_d_end_per_complete: "",
        field_tr_tbl_d_knowledge: "",
        field_tr_tbl_d_evaluation: "",
    });

    const formRef = useRef(null);

    const { loading, data, error, refetch } = useQuery(GET_OUTGOING_TRAINEESHIP, {
        variables: { id: traineeshipID },
        skip: !traineeshipID,
    });

    useEffect(() => {
        if (traineeshipID !== verifiedTraineeshipID) {
            toast.error("Security error: Traineeship ID mismatch", toastConfiguration);
        }
    }, [traineeshipID, verifiedTraineeshipID]);

    useEffect(() => {
        if (data?.traineeship) {
            const traineeship = data.traineeship;
            const changes = traineeship?.final_table_a2;

            const hasChangesTitle = changes?.field_tr_final_tbl_a2_title;
            const title = hasChangesTitle
                ? hasChangesTitle
                : traineeship?.table_a?.field_tr_pre_tbl_a_title || "";

            const orgWebsite = traineeship?.receiving_organization?.field_org_website?.uri
                || traineeship?.receiving_organization?.field_org_website
                || "";

            setCertificateData(prev => ({
                ...prev,
                field_tr_tbl_d_trainee: `${traineeship?.field_tr_student_firstname || ""} ${traineeship?.field_tr_student_lastname || ""}`.trim(),
                field_tr_tbl_d_title: title,
                field_tr_tbl_d_rec_org: traineeship?.receiving_organization?.field_org_name || "",
                field_tr_tbl_d_rec_org_add: traineeship?.receiving_organization?.field_org_address || "",
                field_tr_tbl_d_rec_org_web: orgWebsite,
            }));
        }
    }, [data]);

    const [
        acceptTraineeshipMutation,
        {
            loading: loadingAcceptTraineeship,
            error: errorAcceptTraineeshipMutation,
        },
    ] = useMutation(ACCEPT_TRAINEESHIP);

    const [
        declineTraineeshipMutation,
        {
            loading: loadingDeclineTraineeship,
            error: errorDeclineTraineeshipMutation,
        },
    ] = useMutation(DECLINE_TRAINEESHIP);

    const [
        updateTraineeshipTableDMutation,
        {
            loading: loadingUpdateTableD,
            error: errorUpdateTableD,
        },
    ] = useMutation(UPDATE_TRAINEESHIP_TABLE_D);

    const position = "Supervisor at the Receiving Organization";
    const typeOfStudent = "incoming";
    const institution = 2;

    const shouldDisableSignButtons = (
        signed ||
        StatusActionPair["traineeship-typeOfStudent-0-" + data?.traineeship?.field_tr_status] !== "act"
    );

    const shouldEnableCertificateButton = () => {
        const allowedStatuses = [
            'preliminary_signed-by-student-sending-receiving',
            'final_signed-by-student-sending-receiving'
        ];
        return allowedStatuses.includes(data?.traineeship?.field_tr_status) && 
               !data?.traineeship?.field_tr_status_certificate;
    };

    const handleCloseModalSign = () => setShowModalSign(false);
    const handleShowModalSign = () => setShowModalSign(true);
    const handleCloseModalReject = () => setShowModalReject(false);
    const handleShowModalReject = () => setShowModalReject(true);
    const handleCloseModalCreateCertificate = () => {
        setShowCreateCertificate(false);
        setValidated(false);
    };
    const handleShowModalCreateCertificate = () => setShowCreateCertificate(true);

    const handleCertificateFieldChange = (field, value) => {
        setCertificateData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSignButtonClick = async () => {
        if (data?.traineeship?.traineeshipID !== verifiedTraineeshipID) {
            toast.error("Security error: Cannot sign different traineeship", toastConfiguration);
            return;
        }
        const toastId = toast.loading("Signing Traineeship...", toastConfiguration);
        setSigned(true);
        try {
            let result = await acceptTraineeshipMutation({
                "variables": {
                    "email": guestEmail,
                    "institution": institution,
                    "name": guestName || guestEmail,
                    "traineeshipID": data?.traineeship?.traineeshipID,
                    "position": position,
                    "status": data?.traineeship?.field_tr_status
                }
            });

            handleCloseModalSign();

            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "Successfully signed OLA Traineeship",
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: "Error on signing. Please try again.",
                type: "error",
                isLoading: false,
            });
        }
    };

    const handleDeclineButtonClick = async () => {
        if (data?.traineeship?.traineeshipID !== verifiedTraineeshipID) {
            toast.error("Security error: Cannot decline different traineeship", toastConfiguration);
            return;
        }
        const toastId = toast.loading("Declining Traineeship...", toastConfiguration);
        setSigned(true);
        try {
            let result = await declineTraineeshipMutation({
                "variables": {
                    "email": guestEmail,
                    "institution": institution,
                    "name": guestName || guestEmail,
                    "traineeshipID": data?.traineeship?.traineeshipID,
                    "reason": rejectionMessage,
                    "position": position,
                    "status": data?.traineeship?.field_tr_status
                }
            });

            handleCloseModalReject();

            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "Successfully declined OLA Traineeship",
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: "Error on declining. Please try again.",
                type: "error",
                isLoading: false,
            });
        }
    };

    const hasCertificateData = () => {
        const tableD = data?.traineeship?.table_d;
        if (!tableD) return false;
        
        // Check if table_d exists and has at least one non-empty field
        const hasData = Object.values(tableD).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
        });
        
        return hasData;
    };

    const getPhysicalDateRange = () => {
        const changes = data?.traineeship?.final_table_a2;
        const hasChangesStart = changes?.field_tr_final_tbl_a2_sta_period;
        const hasChangesEnd = changes?.field_tr_final_tbl_a2_end_period;

        if (hasChangesStart && hasChangesEnd) {
            return {
                min: hasChangesStart,
                max: hasChangesEnd,
            };
        }

        const tableA = data?.traineeship?.table_a;
        if (tableA?.field_tr_pre_tbl_a_sta_period && tableA?.field_tr_pre_tbl_a_end_period) {
            return {
                min: tableA.field_tr_pre_tbl_a_sta_period,
                max: tableA.field_tr_pre_tbl_a_end_period,
            };
        }

        return { min: undefined, max: undefined };
    };

    const formatDateLabel = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString + "T00:00:00");
        return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    const physicalDateRange = getPhysicalDateRange();

    const handleCreateCertificateClick = async () => {
        const form = formRef.current;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        const toastId = toast.loading("Submitting certificate...", toastConfiguration);

        try {
            const result = await updateTraineeshipTableDMutation({
                variables: {
                    traineeshipID: traineeshipID,
                    tableDData: certificateData,
                    email: guestEmail,
                    name: guestName,
                    position: position
                }
            });

            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "Successfully submitted certificate",
                    type: "success",
                    isLoading: false,
                });
                handleCloseModalCreateCertificate();
                await refetch(); // Refetch to get updated data
            } else {
                throw new Error("Failed to submit certificate. Please try again.");
            }
        } catch (error) {
            toast.update(toastId, {
                ...toastConfiguration,
                render: `Error while creating certificate: ${error.message}`,
                type: "error",
                isLoading: false,
            });
        }
    };

    if (!traineeshipID) {
        return (
            <div className="text-center text-danger mx-auto" style={{ marginTop: '100px' }}>
                No traineeship ID provided
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-danger mx-auto" style={{ marginTop: '100px' }}>
                Error loading traineeship details: {error.message}
            </div>
        );
    }

    if (loading) {
        return (
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)"
                }}
            >
                <Spinner
                    as="div"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="align-middle my-auto"
                />
                <span>Loading traineeship details...</span>
            </div>
        )
    }

    const certificateFormBody = (
        <Form noValidate validated={validated} ref={formRef}>
            {/* Trainee Name */}
            <Form.Group className="mb-3">
                <Form.Label>Trainee Name *</Form.Label>
                <Form.Control
                    required
                    type="text"
                    value={certificateData.field_tr_tbl_d_trainee}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_trainee', e.target.value)}
                    placeholder="Enter trainee name"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Receiving Organisation */}
            <Form.Group className="mb-3">
                <Form.Label>Receiving Organisation *</Form.Label>
                <Form.Control
                    required
                    type="text"
                    value={certificateData.field_tr_tbl_d_rec_org}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_rec_org', e.target.value)}
                    placeholder="Enter organisation name"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Organisation Sector */}
            <Form.Group className="mb-3">
                <Form.Label>Organisation Sector *</Form.Label>
                <Form.Control
                    required
                    type="text"
                    value={certificateData.field_tr_tbl_d_rec_org_sec}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_rec_org_sec', e.target.value)}
                    placeholder="Enter sector"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Organisation Address */}
            <Form.Group className="mb-3">
                <Form.Label>Organisation Address *</Form.Label>
                <Form.Control
                    required
                    type="text"
                    value={certificateData.field_tr_tbl_d_rec_org_add}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_rec_org_add', e.target.value)}
                    placeholder="Enter address"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Organisation Website */}
            <Form.Group className="mb-3">
                <Form.Label>Organisation Website</Form.Label>
                <Form.Control
                    type="url"
                    value={certificateData.field_tr_tbl_d_rec_org_web}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_rec_org_web', e.target.value)}
                    placeholder="https://example.com"
                />
            </Form.Group>

            {/* Start Date (Complete Period) */}
            <Form.Group className="mb-3">
                <Form.Label>Start Date (Complete Period) *</Form.Label>
                <Form.Control
                    required
                    type="date"
                    value={certificateData.field_tr_tbl_d_sta_per_complete}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_sta_per_complete', e.target.value)}
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* End Date (Complete Period) */}
            <Form.Group className="mb-3">
                <Form.Label>End Date (Complete Period) *</Form.Label>
                <Form.Control
                    required
                    type="date"
                    value={certificateData.field_tr_tbl_d_end_per_complete}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_end_per_complete', e.target.value)}
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Start Date (Physical Presence) */}
            <Form.Group className="mb-3">
                <Form.Label>Start Date (Physical Presence) *</Form.Label>
                <Form.Control
                    required
                    type="date"
                    value={certificateData.field_tr_tbl_d_sta_per_physical}
                    min={physicalDateRange.min}
                    max={physicalDateRange.max}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_sta_per_physical', e.target.value)}
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
                {physicalDateRange.min && physicalDateRange.max && (
                    <Form.Text className="text-muted">
                        Allowed range: {formatDateLabel(physicalDateRange.min)} to {formatDateLabel(physicalDateRange.max)}
                    </Form.Text>
                )}
            </Form.Group>

            {/* End Date (Physical Presence) */}
            <Form.Group className="mb-3">
                <Form.Label>End Date (Physical Presence) *</Form.Label>
                <Form.Control
                    required
                    type="date"
                    value={certificateData.field_tr_tbl_d_end_per_physical}
                    min={physicalDateRange.min}
                    max={physicalDateRange.max}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_end_per_physical', e.target.value)}
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
                {physicalDateRange.min && physicalDateRange.max && (
                    <Form.Text className="text-muted">
                        Allowed range: {formatDateLabel(physicalDateRange.min)} to {formatDateLabel(physicalDateRange.max)}
                    </Form.Text>
                )}
            </Form.Group>

            {/* Traineeship Title */}
            <Form.Group className="mb-3">
                <Form.Label>Traineeship Title *</Form.Label>
                <Form.Control
                    required
                    type="text"
                    value={certificateData.field_tr_tbl_d_title}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_title', e.target.value)}
                    placeholder="Enter traineeship title"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Detailed Programme */}
            <Form.Group className="mb-3">
                <Form.Label>Detailed Programme *</Form.Label>
                <Form.Control
                    required
                    as="textarea"
                    rows={3}
                    value={certificateData.field_tr_tbl_d_program}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_program', e.target.value)}
                    placeholder="Enter programme details"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Knowledge/Skills/Competences */}
            <Form.Group className="mb-3">
                <Form.Label>Knowledge/Skills/Competences *</Form.Label>
                <Form.Control
                    required
                    as="textarea"
                    rows={4}
                    value={certificateData.field_tr_tbl_d_knowledge}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_knowledge', e.target.value)}
                    placeholder="Describe knowledge, skills and competences acquired"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>

            {/* Evaluation */}
            <Form.Group className="mb-3">
                <Form.Label>Evaluation *</Form.Label>
                <Form.Control
                    required
                    as="textarea"
                    rows={4}
                    value={certificateData.field_tr_tbl_d_evaluation}
                    onChange={(e) => handleCertificateFieldChange('field_tr_tbl_d_evaluation', e.target.value)}
                    placeholder="Enter evaluation details"
                />
                <Form.Control.Feedback type="invalid">
                    This field is required.
                </Form.Control.Feedback>
            </Form.Group>
        </Form>
    );

    return (
        <div style={{ padding: '20px' }}>
            <Row style={size !== "md" && size !== "lg" && size !== "xl" && size !== "xxl" ? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 75 }}>
                    <Tabs id="application-info">
                        <CustomTab eventKey="home" title={"TRAINEESHIP INFO"} style={{ background: "#ffff" }}>
                            <TraineeshipInfoTable data={data} size={size} typeOfStudent="incoming" />
                        </CustomTab>
                    </Tabs>
                </Col>
            </Row>
            
            <Row style={size !== "md" && size !== "lg" && size !== "xl" && size !== "xxl" ? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 36 }}>
                    <Tabs id="second-tab">
                        <CustomTab eventKey="general-info" id="general-info" title="General Info" style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="student-info">
                                        <CustomTab eventKey="home" title="Student Personal Data" style={{ background: "#ffff" }}>
                                            <StudentInfo data={data} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="sending-hei">
                                        <CustomTab eventKey="home" title={"Sending Institution"} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"sending"}
                                                size={size}
                                                data={data?.traineeship?.sending_institution}
                                                contact_person={data?.traineeship?.sending_contact_person}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="receiving-org">
                                        <CustomTab eventKey="home" title={"Receiving Organisation"} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"receiving"}
                                                size={size}
                                                data={data?.traineeship?.receiving_organization}
                                                contact_person={data?.traineeship?.receiving_contact_person}
                                                receiving_mentor={data?.traineeship?.receiving_mentor}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        <CustomTab eventKey="programme" id="programme" title={"Traineeship Programme"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="programme-details">
                                        <CustomTab eventKey="home" title={"Programme Details (Table A)"} style={{ background: "#ffff" }}>
                                            <TraineeshipProgrammeTable data={data?.traineeship?.table_a} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        <CustomTab eventKey="changes" id="changes" title={"Changes Traineeship Programme"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="changes-details">
                                        <CustomTab eventKey="home" title={"Changes to Table A"} style={{ background: "#ffff" }}>
                                            <TraineeshipChangesTable data={data?.traineeship?.final_table_a2} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        <CustomTab eventKey="recognition" id="recognition" title={"Recognition & Support"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="recognition-details">
                                        <CustomTab eventKey="home" title={"Sending Institution (Table B)"} style={{ background: "#ffff" }}>
                                            <TraineeshipRecognitionTable
                                                data={data?.traineeship?.table_b}
                                                type={data?.traineeship?.table_b_type?.name}
                                                accidentInsurance={data?.traineeship?.table_b_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="support-details">
                                        <CustomTab eventKey="home" title={"Receiving Organisation (Table C)"} style={{ background: "#ffff" }}>
                                            <TraineeshipSupportTable
                                                data={data?.traineeship?.table_c}
                                                accidentInsurance={data?.traineeship?.table_c_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Certificate Tab - Only show if certificate data exists */}
                        {hasCertificateData() && (
                            <CustomTab eventKey="certificate" id="certificate" title={"Traineeship Certificate"} style={{ background: "#EAECF3" }}>
                                <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                    <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                        <Tabs defaultActiveKey="home" id="certificate-details">
                                            <CustomTab eventKey="home" title={"Certificate (Table D)"} style={{ background: "#ffff" }}>
                                                <CertificateTable data={data?.traineeship?.table_d} size={size} />
                                            </CustomTab>
                                        </Tabs>
                                    </Col>
                                </Row>
                            </CustomTab>
                        )}
                    </Tabs>
                </Col>
            </Row>

            {!(size !== "md" && size !== "lg" && size !== "xl" && size !== "xxl") ?
                <Row style={{ marginLeft: 35, marginRight: 35 }}>
                    <Col style={{ marginTop: 36, display: "flex", justifyContent: "flex-end" }}>
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"decline"}
                            text={"Decline Traineeship"}
                            icon={faXmark}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalReject}
                        />
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"accept"}
                            text={"Sign Traineeship"}
                            styles={{ marginLeft: 20 }}
                            icon={faFileZipper}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalSign}
                        />
                        <CustomButton
                            buttonType={"orange"}
                            text="Create Certificate"
                            icon={faPlus}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            disabled={!shouldEnableCertificateButton()}
                            handleClick={handleShowModalCreateCertificate}
                            styles={{ marginLeft: 20 }}
                        />
                    </Col>
                </Row>
                :
                <Row>
                    <Col style={{ marginTop: 15, paddingLeft: 0, paddingRight: 5 }} xs={6} sm={6}>
                        <div className="col-12">
                            <CustomButton
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                                buttonType={"decline"}
                                text={"Decline"}
                                icon={faXmark}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalReject}
                            />
                        </div>
                    </Col>
                    <Col style={{ marginTop: 15, paddingLeft: 5, paddingRight: 0 }} xs={6} sm={6}>
                        <div className="col-12">
                            <CustomButton
                                buttonType={"accept"}
                                text={"Sign"}
                                icon={faFileZipper}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalSign}
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                            />
                        </div>
                    </Col>
                </Row>
            }

            <CustomModal
                show={showModalReject}
                handleClose={handleCloseModalReject}
                fullscreen={false}
                body={
                    <div>
                        After declining the OLA Traineeship, the student will be able to edit it and introduce your proposed changes. Please fill the comments you want to be emailed to the student in field below:<br />
                        <div style={{ background: "#EAECF3", padding: "5%" }}>
                            <h6 className='h6-style'>Comments</h6>
                            <textarea
                                style={{ width: "100%" }}
                                rows={3}
                                value={rejectionMessage}
                                onChange={(e) => setRejectionMessage(e.target.value)}
                            />
                        </div>
                    </div>
                }
                button={
                    <CustomButton
                        disabled={!rejectionMessage || loadingDeclineTraineeship}
                        buttonType={"decline"}
                        text={"Decline Traineeship"}
                        icon={faXmark}
                        hasIcon={true}
                        iconColor={COLORS.white}
                        handleClick={handleDeclineButtonClick}
                    />
                }
                title={
                    <div>
                        You are about to
                        <span style={{ color: "red" }}> decline </span>
                        a student's OLA Traineeship.
                    </div>
                }
            />

            <CustomModal
                show={showModalSign}
                handleClose={handleCloseModalSign}
                fullscreen={false}
                body={<div>By digitally signing this document, the student, the Sending Institution and the Receiving Institution confirm that they approve the Learning Agreement and that they will comply with all the arrangements agreed by all parties. The Sending Institution and the Receiving Organisation/Enterprise undertake to apply all the principles of the Erasmus Charter for Higher Education relating to mobility for traineeships (or the principles agreed in the applicable inter-institutional or cooperation agreement, where relevant). The Beneficiary Institution and the student also commit to what is set out in the Erasmus+ grant agreement. The Receiving Organisation/Enterprise confirms that the traineeship programme, including the planned tasks, learning outcomes, supervision and mentoring arrangements, and assessment, is feasible and will be provided as described. The Sending Institution commits to recognise the traineeship according to the provisions in Table. The student and the Receiving Organisation/Enterprise will communicate to the Sending Institution any problems or changes regarding the traineeship programme, responsible persons and/or traineeship period.</div>}
                button={
                    <CustomButton
                        disabled={loadingAcceptTraineeship}
                        buttonType={"accept"}
                        text={"Sign Traineeship"}
                        hasIcon={true}
                        iconColor={COLORS.white}
                        icon={faFileZipper}
                        handleClick={handleSignButtonClick}
                    />
                }
                title={
                    <div style={{ paddingBottom: "20px" }}>
                        You are about to
                        <span style={{ color: "green" }}> sign </span>
                        a student's OLA Traineeship.
                    </div>
                }
            />

            <CustomModal
                show={showCreateCertificate}
                handleClose={handleCloseModalCreateCertificate}
                title="Create Certificate"
                subtitle="Fill in the certificate details"
                body={certificateFormBody}
                size="lg"
                button={
                    <CustomButton
                        buttonType={"accept"}
                        text="Submit Certificate"
                        hasIcon={true}
                        iconColor={COLORS.white}
                        handleClick={handleCreateCertificateClick}
                        disabled={loadingUpdateTableD}
                    />
                }
            />

            <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

export default GuestSingleViewTraineeship;